import { IBenefitSelectionRule } from "types/Account/Account";
import {
  BenefitServiceLine,
  BenefitTierPreference,
  IBenefitLineSelection,
  IBenefitLineSelector,
  IBenefitSelectionSaveRequest,
  IBenefitSelectionView,
} from "types/BenefitSelection/BenefitSelection";

import { CONFIGURABLE_BENEFITS } from "./constants";

/**
 * CLE-2792 — the account's configuration as the card edits it, and how it becomes the PUT.
 *
 * 🚨 THE ARRAY'S ORDER IS THE POLICY, IN BOTH DIRECTIONS. The GET returns exceptions already sorted
 * by `priority`, and the PUT carries no `priority` field at all — the API writes `priority = index`.
 * So dragging a row IS the edit, and there is nowhere for a saved order to disagree with the one on
 * screen. The same is true of the payer filters beneath.
 *
 * 🚨 `null` AND `undefined` ARE DIFFERENT VALUES IN `lineSelection` AND THE DIFFERENCE IS LOAD
 * BEARING. A key present with `null` means "use the built-in default"; a key ABSENT means "derive it
 * from the chain", which is the seeder's behaviour and would quietly re-derive a selection a person
 * had deliberately blanked. `toSaveRequest` therefore always sends all three keys.
 */

export interface IBenefitExceptionForm {
  /** From the GET when it exists; a local key for a row added on screen. Never sent — see above. */
  key: string;
  payerClass: string | null;
  payerIds: number[];
  planKeywords: string[];
  /**
   * 🚨 NULL UNTIL SOMEBODY CHOOSES ONE. A new exception used to open with the account's own service
   * line already selected, so pressing Add created a row saying "these payers read exactly what
   * everyone else reads" — a no-op that still sat in the ordered list, above the real rules,
   * looking like a decision. The dialog will not save a row without a line.
   */
  source: BenefitServiceLine | null;
  tierPreference: BenefitTierPreference | null;
}

export interface IBenefitSelectionForm {
  serviceLine: BenefitServiceLine | null;
  tierPreference: BenefitTierPreference;
  physicianCopayBilled: boolean;
  exceptions: IBenefitExceptionForm[];
  /** Keyed by benefit. `null` is a real value: "nothing saved, use the default". */
  lineSelection: Record<string, IBenefitLineSelection | null>;
  /**
   * 🚨 THE PAYER FILTERS LIVE HERE, NOT IN THE ACCOUNT FORM. Until CLE-2792 review round 1 they
   * sat in the account edit form's own field array and "Update Hospital" wrote them, which meant
   * two buttons wrote the same rows and the later one won. The card's Save is now the only writer.
   */
  filters: IBenefitSelectionRule[];
}

let localKeySeq = 0;
export const nextLocalKey = (): string => {
  localKeySeq += 1;
  return `new-${localKeySeq}`;
};

/**
 * One payer filter, normalised to what the API stores.
 *
 * The rows come from the GET and from the filter dialog, which holds whatever was last typed; the
 * API normalises keywords and defaults the network status itself, so this only has to make the
 * shape complete and drop the fields the endpoint does not accept (`id`, `priority`).
 *
 * 🚨 THIS IS A WHITELIST AND THE SAVE IS DELETE-ALL-AND-RECREATE, SO A FIELD IT OMITS IS DESTROYED.
 * It runs in BOTH directions — `toForm` seeds the card from the GET through it, `toSaveRequest`
 * builds the PUT through it — and `applyBenefitSelectionRules` wipes every rule for the account and
 * writes back exactly what arrives. `flatCopayOverride` was missing here while it was present in the
 * OTHER editor (`BenefitSelectionRulesField`, which sits on the same page for everyone), so one
 * person configuring an override and a different person saving this card silently erased it, with
 * no error and nothing on screen. The table and the dialog underneath are shared between the two
 * editors, so the card already SHOWED the override — only the mapping dropped it.
 */
export const toFilterPayload = (rule: any): IBenefitSelectionRule => ({
  payerIds: (rule?.payerIds ?? []).map((payerId: any) =>
    typeof payerId === "number" ? payerId : parseInt(payerId, 10)
  ),
  planKeywords: rule?.planKeywords ?? [],
  matchKeywords: rule?.matchKeywords ?? [],
  excludeKeywords: rule?.excludeKeywords ?? [],
  // 🚨 ALWAYS NULL, NEVER WHAT WAS LOADED. The tier left the payer filter: it is one fact about the
  // facility, set on the service line. A stored `Tier1` on an existing filter is therefore CLEARED
  // by the next save of that account rather than carried through — deliberate, and the API writes
  // `null` too, so neither side can be the one that keeps it alive.
  tierPreference: null,
  networkStatus: rule?.networkStatus || "InNetwork",
  // 🚨 `== null`, NEVER TRUTHINESS — see `toBenefitSelectionRulePayload`. A configured `0` is a
  // payer that charges nothing for these service types, and a `||` here would turn it back into
  // "no override" on every save of an account nobody edited.
  //
  // ⚠️ `Number(...)` BECAUSE THE GET SENDS A PRISMA `Decimal`, WHICH SERIALISES TO A STRING. Passed
  // through as `"100"` it renders correctly and then fails the DTO's `@IsNumber()` on the next
  // save, with a 400 naming a field the operator never typed as text.
  flatCopayOverride:
    rule?.flatCopayOverride == null ? null : Number(rule.flatCopayOverride),
  flatCopayOverrideServiceTypes: rule?.flatCopayOverrideServiceTypes ?? [],
});

/** The GET's view, as the form holds it. Pure — no defaulting that the API would not also apply. */
export const toForm = (view: IBenefitSelectionView): IBenefitSelectionForm => ({
  serviceLine: view.serviceLine.default,
  tierPreference: view.serviceLine.tierPreference,
  physicianCopayBilled: view.serviceLine.physicianCopay.billed,
  exceptions: view.serviceLine.exceptions.map((exception) => ({
    key: exception.id,
    payerClass: exception.payerClass,
    payerIds: [...exception.payerIds],
    planKeywords: [...exception.planKeywords],
    source: exception.source,
    tierPreference: exception.tierPreference,
  })),
  lineSelection: CONFIGURABLE_BENEFITS.reduce(
    (accumulator, benefit) => ({
      ...accumulator,
      [benefit]: view.lineSelection[benefit] ?? null,
    }),
    {} as Record<string, IBenefitLineSelection | null>
  ),
  filters: (view.filters ?? []).map(toFilterPayload),
});

/**
 * The form as the PUT's body.
 *
 * Throws nothing and validates nothing beyond the one field the request cannot be built without:
 * the API is the authority on what a configuration may contain, and a second copy of its rules here
 * would be a second thing to keep in step. The button is what refuses to submit without a service
 * line; this function states the requirement in its type.
 */
export const toSaveRequest = (
  form: IBenefitSelectionForm,
  note?: string
): IBenefitSelectionSaveRequest => ({
  serviceLine: {
    default: form.serviceLine as BenefitServiceLine,
    tierPreference: form.tierPreference,
    physicianCopay: { billed: form.physicianCopayBilled },
    exceptions: form.exceptions.map((exception) => ({
      payerClass: exception.payerClass,
      payerIds: exception.payerIds,
      planKeywords: exception.planKeywords,
      // Never null on a row that reached the list: the dialog refuses to add one without a line.
      source: exception.source as BenefitServiceLine,
      tierPreference: exception.tierPreference ?? form.tierPreference,
    })),
  },
  lineSelection: CONFIGURABLE_BENEFITS.reduce(
    (accumulator, benefit) => ({
      ...accumulator,
      [benefit]: form.lineSelection[benefit] ?? null,
    }),
    {} as Record<string, IBenefitLineSelection | null>
  ),
  filters: form.filters.map(toFilterPayload),
  ...(note ? { note } : {}),
});

/** Moves one entry of an ordered list, which is what a drag is. Returns a new array. */
export const reorder = <T>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0) return items;
  if (from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * A selector as it should be STORED: every field that states nothing is dropped rather than sent
 * empty.
 *
 * 🚨 AN EMPTY LIST IS NOT A CRITERION, AND SENDING ONE MAKES IT LOOK LIKE ONE. Choosing "One of
 * these" for place of service and then picking none left `placeOfService: []` on the selector —
 * which read as a stated criterion to the client's own guard, so a selector that narrowed nothing
 * passed validation, and the sentence describing it claimed a place-of-service rule that named no
 * place. Normalising here means the screen, the guard and the request all agree on what was said.
 */
export const normaliseSelector = (
  selector: IBenefitLineSelector
): IBenefitLineSelector => {
  const next: IBenefitLineSelector = { ...selector };
  if (next.role !== undefined && next.role.trim() === "") delete next.role;
  if (Array.isArray(next.placeOfService) && next.placeOfService.length === 0) {
    delete next.placeOfService;
  }
  if (!next.timePeriodQualifier?.length) delete next.timePeriodQualifier;
  if (!next.coverageLevel?.length) delete next.coverageLevel;
  if (!next.matchKeywords?.length) delete next.matchKeywords;
  if (!next.excludeKeywords?.length) delete next.excludeKeywords;
  return next;
};

/**
 * A selector that states nothing matches NOTHING on every check, forever, silently.
 *
 * The API refuses to store one (`validateLineSelection`), and this is the same predicate stated on
 * the client so an editor is told at the point of typing rather than by a 400 after saving. It is
 * deliberately the SAME rule and not a stricter one — a screen that refused something the API
 * accepts would make a configuration unreachable with no way to see why. It reads the NORMALISED
 * selector, which is also what gets sent, so the two cannot disagree.
 */
export const selectorStatesSomething = (raw: IBenefitLineSelector): boolean => {
  const selector = normaliseSelector(raw);
  if (selector.any === true) return true;
  if (selector.role !== undefined) return true;
  if (selector.placeOfService !== undefined) return true;
  if (selector.timePeriodQualifier?.length) return true;
  if (selector.coverageLevel?.length) return true;
  if (selector.matchKeywords?.length) return true;
  if (selector.excludeKeywords?.length) return true;
  return false;
};

/**
 * CLE-2755 — one payer filter as the account GET returns it, seeded into the edit form.
 *
 * 🚨 THE INVERSE OF `toBenefitSelectionRulePayload`, AND KEPT BESIDE IT SO THE PAIR CANNOT DRIFT.
 * The two are a round trip: the form is seeded from the account, `SubmitEdit` sends that state
 * back, and the API WIPES AND REWRITES every rule for the account. So a field this function drops
 * is not merely hidden on screen — it survives until the next time anybody presses "Update
 * Hospital" for any reason at all, and is then destroyed, with no error and nothing having
 * mentioned it.
 *
 * That is not hypothetical. `flatCopayOverride` shipped missing from this whitelist and was caught
 * on dev by saving an override, reloading the page, and seeing the column read "—".
 *
 * ⚠️ `Number(...)` BECAUSE THE API SENDS A PRISMA `Decimal`, WHICH SERIALISES TO A STRING. Passed
 * through as `"100"` it renders correctly and then fails the DTO's `@IsNumber()` on the next save,
 * with a 400 the operator cannot act on.
 */
export const seedBenefitSelectionRuleFromAccount = (rule: any) => ({
  payerIds: rule.payerIds ?? [],
  planKeywords: rule.planKeywords ?? [],
  matchKeywords: rule.matchKeywords ?? [],
  excludeKeywords: rule.excludeKeywords ?? [],
  // The tier is deliberately NOT seeded — see `toFilterPayload`. Nothing on screen shows it and
  // nothing sends it; a stored value is cleared on the next save.
  networkStatus: rule.networkStatus ?? "InNetwork",
  createdAt: rule.createdAt ?? undefined,
  flatCopayOverride:
    rule.flatCopayOverride == null ? null : Number(rule.flatCopayOverride),
  flatCopayOverrideServiceTypes: rule.flatCopayOverrideServiceTypes ?? [],
});

/**
 * CLE-2755 — one payer filter as the API is sent it.
 *
 * 🚨 EXTRACTED FOR THE SAME REASON `buildBenefitSelectionRuleRows` WAS: a mapping written inline in
 * a save handler cannot be tested without mocking a mutation, a toast and a router, so it is where
 * a `||` creeps in and stays. This one has a specific thing to protect — a configured `$0` copay
 * override, which `||` turns back into "no override" on every save of an untouched account.
 */
export const toBenefitSelectionRulePayload = (rule: any) => ({
  payerIds: (rule.payerIds || []).map((payerId: any) => parseInt(payerId, 10)),
  planKeywords: rule.planKeywords || [],
  matchKeywords: rule.matchKeywords || [],
  excludeKeywords: rule.excludeKeywords || [],
  // Always null — see `toFilterPayload`. The tier is not a payer-filter field any more.
  tierPreference: null,
  networkStatus: rule.networkStatus || "InNetwork",
  // `??`, never `||` — see above.
  flatCopayOverride: rule.flatCopayOverride ?? null,
  flatCopayOverrideServiceTypes: rule.flatCopayOverrideServiceTypes || [],
});

/**
 * A filter that narrows nothing is the empty filter Ani saved once on dev. Save is refused.
 *
 * 🚨 CLE-2755 — A COPAY OVERRIDE COUNTS AS SAYING SOMETHING, and it is tested with `!= null` rather
 * than folded into the `||` chain above. A rule whose only content is "these patients owe $0" is a
 * complete, intentional rule; truthiness would score it as empty and refuse the save, which is the
 * one case an operator would be least able to explain.
 *
 * 🚨 A TIER NO LONGER COUNTS, BECAUSE A FILTER CAN NO LONGER CARRY ONE. Nothing on the screen sets
 * it and the API writes `null` whatever arrives, so scoring a submitted tier as narrowing would
 * accept a filter that reaches the table completely empty.
 */
export const filterStatesSomething = (rule: any): boolean =>
  Boolean(
    rule?.payerIds?.length ||
      rule?.planKeywords?.length ||
      rule?.matchKeywords?.length ||
      rule?.excludeKeywords?.length ||
      (rule?.networkStatus && rule.networkStatus !== "InNetwork")
  ) || rule?.flatCopayOverride != null;

/**
 * WHAT HAPPENS WHEN NO SELECTOR MATCHES — one setting per benefit, stored as the list's shape.
 *
 * 🚨 A TRAILING `{ any: true }` IS NOT A SELECTOR AND THE EDITOR NO LONGER SHOWS IT AS ONE. It is
 * the whole difference between "any line will do" and "quote nothing", which used to be expressed
 * by adding or removing an entry called the catch-all — a mechanism that had to be explained before
 * it could be used, and read as an eighth selector kind rather than as the choice it is.
 *
 * `ANY_LINE` = append `{ any: true }` last. `NO_VALUE` = no trailing `any`. Nothing else changes,
 * so a list saved before this and a list saved after it are the same rows on the wire.
 */
export type LineFallback = "ANY_LINE" | "NO_VALUE";

/** Copay and co-insurance take whatever the payer sent; a physician copay is never guessed. */
export const DEFAULT_FALLBACK: Record<string, LineFallback> = {
  FLAT_COPAY: "ANY_LINE",
  COINSURANCE_PERCENT: "ANY_LINE",
  PROFESSIONAL_COPAY: "NO_VALUE",
};

export const endsWithAnyLine = (selection: IBenefitLineSelection): boolean =>
  selection.selectors.length > 0 &&
  selection.selectors[selection.selectors.length - 1]?.any === true;

export const fallbackOf = (selection: IBenefitLineSelection): LineFallback =>
  endsWithAnyLine(selection) ? "ANY_LINE" : "NO_VALUE";

/** The selectors a person actually wrote — the trailing `any` stripped back out. */
export const selectorsWithoutFallback = (
  selection: IBenefitLineSelection
): IBenefitLineSelector[] =>
  endsWithAnyLine(selection)
    ? selection.selectors.slice(0, -1)
    : [...selection.selectors];

/** The list as the API stores it, with the fallback put back on the end. */
export const withFallback = (
  selection: IBenefitLineSelection,
  fallback: LineFallback
): IBenefitLineSelection => {
  const selectors = selectorsWithoutFallback(selection);
  return {
    ...selection,
    selectors:
      fallback === "ANY_LINE" ? [...selectors, { any: true }] : selectors,
  };
};
